import { PDFDocument } from 'pdf-lib';
import { buildChecklistPdfDoc, type GenerateChecklistPdfParams as BuilderParams } from './checklistPdfDoc';
import { databaseService } from './databaseService';
import APP_CONSTANTS from '@/config/constants';
import { Checklist, ChecklistField } from '@/hooks/useChecklistEditor';
import { PDFServiceWebLogger } from '@/utils/logger';
import { dispatchSilentPdfDownloadEvent, PdfDownloadPlatform } from '@/utils/pdfDownloadEvents';

/**
 * Parâmetros para geração de PDF de checklist.
 */
export interface GenerateChecklistPdfParams extends BuilderParams {
  checklist: Checklist;
  template: ChecklistField[];
  values: Record<string, string | null | undefined> | null;
}

/**
 * Dados mínimos da empresa necessários para o cabeçalho
 */
interface EmpresaInfo {
  id: string;
  nome?: string;
  cnpj?: string;
  email?: string;
  telefone?: string;
  endereco?: string;
  cep?: string;
  cidade?: string;
  uf?: string;
  logo_url?: string;
}

// Alias para compatibilidade com código legado
// (apontamos para o builder extraído para o arquivo compartilhado)
export const internalGenerateChecklistPdfDoc = buildChecklistPdfDoc;

/**
 * Gera e faz download de um PDF elegante e minimalista representando o checklist.
 * Esta implementação é exclusiva para web (arquivo .web.ts) e utiliza pdf-lib + file-saver.
 */
const isIosWeb = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || '';
  const isIOS = /iPad|iPhone|iPod/i.test(ua);
  const isIPadOs13Up = /Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  return isIOS || isIPadOs13Up;
};

const isMobileWeb = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || navigator.platform || '';
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const iPadDesktopMode = /Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  return mobileRegex.test(ua) || iPadDesktopMode;
};

const resolveDownloadHint = () => {
  if (typeof navigator === 'undefined') return 'O PDF foi salvo na pasta Downloads do seu dispositivo.';
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) {
    return 'O PDF foi salvo na pasta Downloads do Android. Você também pode abrir pela notificação do navegador.';
  }
  return 'O PDF foi salvo na pasta Downloads do seu dispositivo.';
};

const detectWebPlatform = (): PdfDownloadPlatform => {
  if (typeof navigator === 'undefined') return 'unknown-web';
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android-mobile-web';
  if (isIosWeb()) return 'ios-mobile-web';
  return 'desktop-web';
};

const notifySilentDownload = (blob: Blob, fileName: string) => {
  if (!isMobileWeb() || isIosWeb()) {
    PDFServiceWebLogger.Main.info('[PDF] Notificação pós-download não exibida (não é mobile web ou já houve abertura em nova aba)');
    return;
  }

  try {
    const blobUrl = URL.createObjectURL(blob);
    dispatchSilentPdfDownloadEvent({
      fileName,
      blob,
      blobUrl,
      messageHint: resolveDownloadHint(),
      platform: detectWebPlatform(),
    });
    setTimeout(() => {
      PDFServiceWebLogger.Main.info('[PDF] Liberando blob URL de prompt pós-download');
      URL.revokeObjectURL(blobUrl);
    }, 120000);
  } catch (error) {
    PDFServiceWebLogger.Main.error('[PDF] Falha ao preparar alerta pós-download', { error });
  }
};

export async function generateChecklistPdfWeb(params: GenerateChecklistPdfParams) {
  const pdfDoc = await buildChecklistPdfDoc(params);

  // 5) Salvar PDF e disparar download
  const { checklist } = params;
  PDFServiceWebLogger.Main.info('[PDF] Salvando PDF e iniciando download');
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
  const fileName = `checklist-${checklist.id}.pdf`;

  if (isIosWeb()) {
    PDFServiceWebLogger.Main.info('[PDF] Ambiente iOS web detectado, utilizando fallback com window.open');
    const blobUrl = URL.createObjectURL(blob);
    const releaseUrl = () => {
      PDFServiceWebLogger.Main.info('[PDF] Liberando blob URL');
      URL.revokeObjectURL(blobUrl);
    };
    setTimeout(releaseUrl, 60000);
    const popup = window.open(blobUrl, '_blank');
    if (popup) {
      popup.addEventListener('load', () => {
        PDFServiceWebLogger.Main.info('[PDF] Nova aba carregada com PDF');
        releaseUrl();
      });
    } else {
      PDFServiceWebLogger.Main.info('[PDF] window.open bloqueado, usando file-saver como fallback secundário');
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { saveAs } = require('file-saver');
      saveAs(blob, fileName);
    }
    return;
  }

  // Importa file-saver dinamicamente para evitar que seu bundle quebre no mobile
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { saveAs } = require('file-saver');
  saveAs(blob, fileName);
  notifySilentDownload(blob, fileName);
  PDFServiceWebLogger.Main.info('[PDF] Ambiente web padrão detectado, download iniciado', { checklistId: checklist.id, userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown' });
}

// ------------------ Helpers ------------------ //
async function fetchArrayBuffer(url: string): Promise<ArrayBuffer> {
  if (url.startsWith('data:')) {
    // Base64 embutido
    const base64 = url.split(',')[1];
    const binary = atob(base64);
    const len = binary.length;
    const arr = new Uint8Array(len);
    for (let i = 0; i < len; i++) arr[i] = binary.charCodeAt(i);
    return arr.buffer;
  }
  const res = await fetch(url);
  if (!res.ok) throw new Error('Falha ao baixar recurso');
  return res.arrayBuffer();
}

function formatDate(ts: any): string {
  PDFServiceWebLogger.Main.info('[PDF] formatDate chamada', { ts, typeof_ts: typeof ts });
  if (!ts) return '';
  let d: Date;
  if (typeof ts === 'number') {
    d = new Date(ts);
  } else if (typeof ts === 'string') {
    // Tenta parsear como ISO, depois como número
    if (/^\d+$/.test(ts)) {
      d = new Date(Number(ts));
    } else {
      d = new Date(ts);
    }
  } else {
    return '';
  }
  if (isNaN(d.getTime())) return '';
  // Formatar como dd/mm/yy - HH:mm
  const pad = (n: number) => n.toString().padStart(2, '0');
  const dia = pad(d.getDate());
  const mes = pad(d.getMonth() + 1);
  const ano = d.getFullYear().toString().slice(-2);
  const hora = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${dia}/${mes}/${ano} - ${hora}:${min}`;
}

function formatDateFull(ts: any): string {
  PDFServiceWebLogger.Main.info('[PDF] formatDateFull chamada', { ts, typeof_ts: typeof ts });
  if (!ts) return '';
  let d: Date;
  if (typeof ts === 'number') {
    d = new Date(ts);
  } else if (typeof ts === 'string') {
    // Tenta parsear como ISO, depois como número
    if (/^\d+$/.test(ts)) {
      d = new Date(Number(ts));
    } else {
      d = new Date(ts);
    }
  } else {
    return '';
  }
  if (isNaN(d.getTime())) return '';
  // Formatar como dd/mm/yyyy - HH:mm
  const pad = (n: number) => n.toString().padStart(2, '0');
  const dia = pad(d.getDate());
  const mes = pad(d.getMonth() + 1);
  const ano = d.getFullYear().toString();
  const hora = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${dia}/${mes}/${ano} - ${hora}:${min}`;
} 
